import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import Tab from "components/DesignSystem/Tab/Tab";
import { Button } from "components/DesignSystem/Button/Button";
import { PdfScrollTarget } from "components/PdfViewer/PdfViewer";
import { DocViewer } from "components/DocViewer/DocViewer";
import ToolTip from "components/design/toolTip";
import { Cross } from "components/icons/Cross";

export type AnnotationDoc = {
  id: string;
  name: string;
  previewUrl: string;
  fileType: string;
};

export type DocumentPin = {
  id: string;
  docIndex: number;
  x: number;
  y: number;
  pageNumber: number;
  number: number;
  isActive: boolean;
};

type DocumentAnnotationPanelProps = {
  docs: AnnotationDoc[];
  activeDocIndex: number;
  onTabChange: (index: number) => void;
  pins: DocumentPin[];
  onPinClick: (pinId: string) => void;
  onDocumentClick?: (
    x: number,
    y: number,
    docIndex: number,
    pageNumber: number
  ) => void;
  headerExtra?: React.ReactNode;
  pageOverlayExtra?: (pageNumber: number) => React.ReactNode;
  groupId?: string;
  onDocRemove?: (index: number) => void;
};

export const DocumentAnnotationPanel = ({
  docs,
  activeDocIndex,
  onTabChange,
  pins,
  onPinClick,
  onDocumentClick,
  headerExtra,
  pageOverlayExtra,
  groupId,
  onDocRemove,
}: DocumentAnnotationPanelProps) => {
  const activeDoc = docs[activeDocIndex];
  const activeTabValue = activeDoc?.id ?? "";

  const panelRef = useRef<HTMLDivElement>(null);
  const [panelWidth, setPanelWidth] = useState(0);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setPanelWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const activePin =
    pins.find((p) => p.isActive && p.docIndex === activeDocIndex) ?? null;
  const scrollTo = useMemo<PdfScrollTarget | null>(
    () =>
      activePin ? { pageNumber: activePin.pageNumber, y: activePin.y } : null,
    [activePin]
  );

  if (docs.length === 0) {
    return (
      <div className="t-flex t-items-center t-justify-center t-h-full t-text-body-sm t-text-text-30">
        No documents uploaded
      </div>
    );
  }

  return (
    <div ref={panelRef} className="t-flex t-flex-col t-h-full t-w-full">
      <Tab.Root
        value={activeTabValue}
        className="t-flex t-flex-col t-h-full t-w-full"
      >
        <div className="t-border-0 t-border-b t-border-solid t-border-neutral-10 t-box-border t-sticky t-top-0 t-bg-white t-z-10 t-flex t-items-end t-gap-2 t-h-12">
          <div className="t-flex-1 t-overflow-x-auto t-px-4 t-pt-2 t-min-w-0">
            <Tab.List className="!t-border-none !t-mb-0">
              {docs.map((doc, index) => {
                const handleTabClick = () => onTabChange(index);
                const handleRemoveClick = (
                  event: React.MouseEvent<HTMLButtonElement>
                ) => {
                  event.stopPropagation();
                  onDocRemove?.(index);
                };

                return (
                  <Tab.Trigger
                    key={doc.id}
                    value={doc.id}
                    onClick={handleTabClick}
                  >
                    <span className="t-flex t-items-center t-gap-1">
                      <ToolTip text={doc.name}>
                        <span className="t-text-body-sm t-truncate t-max-w-40">
                          {doc.name}
                        </span>
                      </ToolTip>
                      {onDocRemove && (
                        <ToolTip text="Remove document">
                          <Button
                            type="button"
                            customType="ghost_icon"
                            size="xxsmall"
                            onClick={handleRemoveClick}
                          >
                            <Cross size="12" />
                          </Button>
                        </ToolTip>
                      )}
                    </span>
                  </Tab.Trigger>
                );
              })}
            </Tab.List>
          </div>
          <div className="t-relative t-shrink-0 t-flex t-items-center t-pr-4 t-pb-2.5">
            <div className="t-absolute t-right-full t-top-0 t-bottom-0 t-w-12 t-pointer-events-none t-bg-gradient-to-l t-from-white t-to-transparent" />
            {headerExtra}
          </div>
        </div>

        {docs.map((doc, index) => {
          const docPins = pins.filter((p) => p.docIndex === index);
          const isActive = index === activeDocIndex;

          return (
            <Tab.Content
              key={doc.id}
              value={doc.id}
              forceMount
              className={classNames("t-flex-1 t-overflow-hidden", {
                "t-invisible t-absolute t-pointer-events-none": !isActive,
              })}
            >
              <DocViewer
                url={doc.previewUrl || ""}
                fileType={doc.fileType}
                name={doc.name}
                fileId={doc.previewUrl ? undefined : doc.id}
                groupId={groupId}
                containerWidth={panelWidth}
                scrollTo={isActive ? scrollTo : null}
                pins={docPins}
                onPinClick={onPinClick}
                onDocumentClick={
                  onDocumentClick
                    ? (x, y, pageNumber) =>
                        onDocumentClick(x, y, index, pageNumber)
                    : undefined
                }
                pageOverlayExtra={isActive ? pageOverlayExtra : undefined}
              />
            </Tab.Content>
          );
        })}
      </Tab.Root>
    </div>
  );
};
